import classNames from 'classnames';
import { forwardRef } from 'react';

import IconCheckCCC from '!url-loader!../assets/icons/VF_check_ccc.svg';
import IconCheck from '!url-loader!../assets/icons/VF_check_rgb.svg';

import { css } from '../stitches.config';
import { theme } from '../theme';
import { HTMLTag } from '../types';
import { Box, BoxProps } from './Box';

export type CheckboxProps<C extends HTMLTag = 'input'> = {
  checked: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
} & BoxProps<C>;

export const Checkbox = forwardRef<HTMLElement, CheckboxProps<HTMLTag>>(function Checkbox(
  { as = 'input', size = 'lg', checked, ...props },
  ref,
) {
  return (
    <Box
      as={as}
      checked={checked}
      type='checkbox'
      className={classNames(
        css({
          cursor: 'pointer',
          appearance: 'none',
          WebkitAppearance: 'none',
          background: '$background',
          border: '1px solid #a4a9ac',
          borderRadius: '4px',
          lineHeight: '0.8',
          position: 'relative',
          verticalAlign: 'middle',

          '&:after': {
            content: `url(${IconCheck})`,
            width: '70%',
            height: '70%',
            opacity: '0',
            fontSize: '1.125rem',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%) scale(0)',
            transition: 'transform 0.3s ease-in-out',
          },

          '&:hover&:not(:disabled)&:not(:checked)': {
            '&:after': {
              transform: 'translate(-50%,-50%) scale(1)',
              opacity: '1',
              content: `url(${IconCheckCCC})`,
            },
          },

          '&:checked': {
            '&:after': {
              transform: 'translate(-50%,-50%) scale(1)',
              opacity: '1',
            },
          },

          '&:disabled': {
            backgroundColor: '#f1f1f1',
            border: '1px solid #bcbcbc',
            cursor: 'auto',

            '&:after': {
              content: `url(${IconCheckCCC})`,
            },
          },

          variants: {
            size: Object.fromEntries(
              (['sm', 'md', 'lg', 'xl'] as const).map((size) => [size, {
                height: `calc(${theme.fontSizes[size]} * 1.4)`,
                width: `calc(${theme.fontSizes[size]} * 1.4)`,
              }]),
            ),
          },
        })({ size }),
      )}
      ref={ref}
      {...props}
    />
  );
});
